"use no memo";

import { SectionSchema } from "@repo/validators";
import { useTranslation } from "react-i18next";

import type { Section } from "~/providers/section-provider";
import { useSectionManager } from "~/managers/use-section-manager";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
} from "../ui/form";
import { Input } from "../ui/input";
import { ModalInput } from "../ui/modal-input";

export default function SectionForm({
  section,
  onClose,
}: {
  section?: Section;
  onClose: (force?: boolean) => void;
}) {
  const { createSection, updateSection } = useSectionManager();
  const { t, i18n } = useTranslation("common");
  const form = useForm({
    reValidateMode: "onChange",
    schema: SectionSchema(i18n),
    defaultValues: {
      name: section?.name ?? "",
      position: section?.position ?? 1,
      width: section?.width ?? 500,
      height: section?.height ?? 500,
    },
  });

  const handleSubmit = form.handleSubmit(async (formData) => {
    if (section) {
      const { data, error } = await updateSection({
        id: section.id,
        ...formData,
      });
      if (data) {
        form.reset({}, { keepValues: true });
        onClose(true);
      } else if (error) {
        form.setError(error.path, { message: error.message });
      }
    } else {
      const { data, error } = await createSection(formData);
      if (data) {
        form.reset({}, { keepValues: true });
        onClose(true);
      } else if (error) {
        form.setError(error.path, { message: error.message });
      }
    }
  });
  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit}
        id="section-form"
        className="grid grid-cols-2 gap-2"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Section.name")}</FormLabel>
              <FormControl>
                <Input {...field} placeholder="Name" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="position"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Section.position")}</FormLabel>
              <FormControl>
                <ModalInput {...field} type="number" min={1} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="width"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Section.width")}</FormLabel>
              <FormControl>
                <ModalInput {...field} type="number" min={1} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="height"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Section.height")}</FormLabel>
              <FormControl>
                <ModalInput {...field} type="number" min={1} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}
